import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { MOVEMENT_TYPE_LABELS, stockAdjustmentSchema, type LotDTO, type MovementDTO, type StockRowDTO } from '@bifeai/shared';
import {
  Button,
  Callout,
  Card,
  CardBody,
  Count,
  Dialog,
  EmptyState,
  Field,
  Input,
  Kpi,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { Boxes, ClipboardCheck, Plus } from 'lucide-react';
import { useDeferredValue, useState } from 'react';
import { useNavigate } from 'react-router';
import { ProductThumb, SearchBox, StockStatusBadge, Toolbar } from '../components/inventory';
import { api, errorMessage } from '../lib/api';
import { invalidateInventory, useCategories } from '../lib/catalog';
import { formatDate, formatDateTime, formatMoney } from '../lib/format';
import { useSession } from '../lib/session';
import { daysUntil, formatQty, fromInternalQty, toInternalQty } from '../lib/units';

type Filter = 'all' | 'alerts' | 'expiring' | 'low' | 'supplies';

const ORIGIN_LABELS: Record<string, string> = {
  PURCHASE: 'Compra',
  ADJUSTMENT: 'Ajuste',
  PRODUCTION: 'Producción',
  CUTTING: 'Desposte',
  TRANSFER: 'Transferencia',
};

function expiryText(iso: string | null): { text: string; className?: string } {
  if (!iso) return { text: '—' };
  const d = daysUntil(iso);
  if (d < 0)
    return {
      text: `Venció hace ${-d} ${d === -1 ? 'día' : 'días'}`,
      className: 'bf-text-danger',
    };
  if (d === 0) return { text: 'Vence hoy', className: 'bf-text-danger' };
  if (d === 1) return { text: 'Vence mañana', className: 'bf-text-warning' };
  if (d <= 3) return { text: `En ${d} días`, className: 'bf-text-warning' };
  return { text: formatDate(iso) };
}

export function StockPage() {
  const navigate = useNavigate();
  const { can } = useSession();
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [detail, setDetail] = useState<StockRowDTO | null>(null);
  const deferred = useDeferredValue(search.trim());
  const categories = useCategories();

  const qs = new URLSearchParams();
  if (deferred) qs.set('search', deferred);
  if (categoryId) qs.set('categoryId', categoryId);
  const stock = useQuery({
    queryKey: ['stock', { search: deferred, categoryId }],
    queryFn: () => api.get<StockRowDTO[]>(`/stock${qs.size ? `?${qs.toString()}` : ''}`),
    placeholderData: keepPreviousData,
  });

  const rows = stock.data ?? [];
  const canCost = can('costs.view');
  const counts = {
    expiring: rows.filter((r) => r.status === 'EXPIRING' || r.status === 'EXPIRED').length,
    low: rows.filter((r) => r.status === 'LOW' || r.status === 'OUT' || r.status === 'NEGATIVE').length,
  };
  const alerts = counts.expiring + counts.low;
  const value = rows.reduce((a, r) => a + (r.valueCents ?? 0), 0);
  const visible = rows.filter((r) => {
    if (filter === 'supplies') return r.kind === 'SUPPLY' || !r.sellable;
    if (filter === 'alerts') return r.status !== 'OK';
    if (filter === 'expiring') return r.status === 'EXPIRING' || r.status === 'EXPIRED';
    if (filter === 'low') return r.status === 'LOW' || r.status === 'OUT' || r.status === 'NEGATIVE';
    return true;
  });

  return (
    <>
      <PageHeader
        title="Stock"
        subtitle="Lo que hay en cámara y mostrador, por lote. Se descuenta primero lo que vence antes."
        actions={
          can('purchases.manage') && (
            <Button variant="primary" icon={<Plus />} onClick={() => navigate('/compras/nueva')}>
              Cargar compra
            </Button>
          )
        }
      />

      <div className="bf-kpi-grid">
        {canCost && <Kpi label="Valor del stock (a costo)" value={formatMoney(value)} delta="Según el costo de cada lote" deltaMuted />}
        <Kpi
          label="Productos con stock"
          value={String(rows.filter((r) => r.qty > 0).length)}
          tone="green"
          delta={`de ${rows.length} activos`}
          deltaMuted
        />
        <Kpi
          label="Por vencer o vencidos"
          value={String(counts.expiring)}
          tone={counts.expiring ? 'amber' : 'green'}
          delta="Mirá los lotes para usarlos primero"
          deltaMuted
        />
        <Kpi
          label="Stock bajo o sin stock"
          value={String(counts.low)}
          tone={counts.low ? 'red' : 'green'}
          delta="Según el mínimo de cada producto"
          deltaMuted
        />
      </div>

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Buscar producto" />
        <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} aria-label="Categoría">
          <option value="">Todas las categorías</option>
          {categories.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <div className="bf-chips" role="group" aria-label="Filtrar">
          <button type="button" className="bf-chip" aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
            Todos
          </button>
          <button type="button" className="bf-chip" aria-pressed={filter === 'alerts'} onClick={() => setFilter('alerts')}>
            Con alertas <Count value={alerts} />
          </button>
          <button type="button" className="bf-chip" aria-pressed={filter === 'expiring'} onClick={() => setFilter('expiring')}>
            Por vencer
          </button>
          <button type="button" className="bf-chip" aria-pressed={filter === 'low'} onClick={() => setFilter('low')}>
            Stock bajo
          </button>
          <button type="button" className="bf-chip" aria-pressed={filter === 'supplies'} onClick={() => setFilter('supplies')}>
            Insumos
          </button>
        </div>
      </Toolbar>

      <Card>
        <CardBody flush>
          {stock.isPending ? (
            <div style={{ padding: '8px 22px 16px', display: 'grid', gap: 12 }}>
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </div>
          ) : visible.length === 0 ? (
            <EmptyState
              icon={<Boxes />}
              title={rows.length === 0 ? 'Todavía no hay productos' : filter === 'all' ? 'Sin resultados' : 'Nada para revisar'}
              text={rows.length === 0 ? 'Cargá productos y después tus compras para ver el stock.' : undefined}
            />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Stock</th>
                    <th>Mínimo</th>
                    <th>Próximo vencimiento</th>
                    <th>Estado</th>
                    {canCost && <th style={{ textAlign: 'right' }}>Valor</th>}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => {
                    const exp = expiryText(r.nextExpiry);
                    return (
                      <tr key={r.productId} data-clickable="true" onClick={() => setDetail(r)}>
                        <td>
                          <div className="bf-cell-product">
                            <ProductThumb photoUrl={r.photoUrl} icon={r.categoryIcon} name={r.name} size="sm" />
                            <div>
                              <span className="bf-cell-main">{r.name}</span>
                              <div className="bf-cell-sub">
                                {r.categoryName ?? 'Sin categoría'} · {r.lots} {r.lots === 1 ? 'lote' : 'lotes'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className={`bf-num ${r.qty < 0 ? 'bf-text-danger' : ''}`} style={{ fontWeight: 600 }}>
                          {formatQty(r.unitType, r.qty)}
                        </td>
                        <td className="bf-num bf-cell-sub">{r.minStock > 0 ? formatQty(r.unitType, r.minStock) : '—'}</td>
                        <td className={exp.className}>{exp.text}</td>
                        <td>
                          <StockStatusBadge status={r.status} />
                        </td>
                        {canCost && (
                          <td className="bf-num" style={{ textAlign: 'right' }}>
                            {formatMoney(r.valueCents ?? 0)}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {detail && <StockDetailDialog row={detail} onClose={() => setDetail(null)} />}
    </>
  );
}

function StockDetailDialog({ row, onClose }: { row: StockRowDTO; onClose: () => void }) {
  const { can, multiBranch, selectedBranchId } = useSession();
  const [tab, setTab] = useState<'lots' | 'moves'>('lots');
  const [adjusting, setAdjusting] = useState(false);
  const canCost = can('costs.view');
  const showBranch = multiBranch && !selectedBranchId;

  const lots = useQuery({
    queryKey: ['stock', 'lots', row.productId],
    queryFn: () => api.get<LotDTO[]>(`/stock/${row.productId}/lots`),
  });
  const moves = useQuery({
    queryKey: ['stock', 'moves', row.productId],
    queryFn: () => api.get<MovementDTO[]>(`/stock/${row.productId}/movements`),
    enabled: tab === 'moves',
  });

  if (adjusting) return <AdjustDialog row={row} lots={lots.data ?? []} onClose={() => setAdjusting(false)} onDone={onClose} />;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={row.name} description={`Stock: ${formatQty(row.unitType, row.qty)}`} wide>
      <div className="bf-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'lots'} onClick={() => setTab('lots')}>
          Lotes
        </button>
        <button type="button" role="tab" aria-selected={tab === 'moves'} onClick={() => setTab('moves')}>
          Movimientos
        </button>
      </div>

      {tab === 'lots' ? (
        lots.isPending ? (
          <Skeleton />
        ) : (lots.data?.length ?? 0) === 0 ? (
          <EmptyState icon={<Boxes />} title="Sin lotes en stock" text="Los lotes se crean al cargar una compra." />
        ) : (
          <div className="bf-scroll-box">
            <table className="bf-table">
              <thead>
                <tr>
                  <th>Ingreso</th>
                  {showBranch && <th>Sucursal</th>}
                  <th>Origen</th>
                  <th>Vence</th>
                  <th>Queda</th>
                  {canCost && <th>Costo</th>}
                </tr>
              </thead>
              <tbody>
                {lots.data?.map((l) => {
                  const exp = expiryText(l.expiresAt);
                  return (
                    <tr key={l.id}>
                      <td className="bf-cell-sub">{formatDate(l.receivedAt)}</td>
                      {showBranch && <td>{l.branchName}</td>}
                      <td>
                        {ORIGIN_LABELS[l.origin] ?? l.origin}
                        {l.supplierName && <div className="bf-cell-sub">{l.supplierName}</div>}
                      </td>
                      <td className={exp.className}>{exp.text}</td>
                      <td className={`bf-num ${l.qtyOnHand < 0 ? 'bf-text-danger' : ''}`}>
                        {formatQty(row.unitType, l.qtyOnHand)}
                        <div className="bf-cell-sub">de {formatQty(row.unitType, l.initialQty)}</div>
                      </td>
                      {canCost && <td className="bf-num">{l.unitCostCents !== null ? formatMoney(l.unitCostCents) : '—'}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : moves.isPending ? (
        <Skeleton />
      ) : (moves.data?.length ?? 0) === 0 ? (
        <EmptyState icon={<Boxes />} title="Sin movimientos" />
      ) : (
        <div className="bf-scroll-box">
          <table className="bf-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Movimiento</th>
                {showBranch && <th>Sucursal</th>}
                <th style={{ textAlign: 'right' }}>Cantidad</th>
                <th>Quién</th>
              </tr>
            </thead>
            <tbody>
              {moves.data?.map((m) => (
                <tr key={m.id}>
                  <td className="bf-cell-sub">{formatDateTime(m.createdAt)}</td>
                  <td>
                    {MOVEMENT_TYPE_LABELS[m.type] ?? m.type}
                    {m.reason && <div className="bf-cell-sub">{m.reason}</div>}
                  </td>
                  {showBranch && <td>{m.branchName}</td>}
                  <td className={`bf-num ${m.qty < 0 ? 'bf-text-danger' : 'bf-text-success'}`} style={{ textAlign: 'right' }}>
                    {m.qty > 0 ? '+' : ''}
                    {formatQty(row.unitType, m.qty)}
                  </td>
                  <td className="bf-cell-sub">{m.createdByName ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {can('purchases.manage') && (
        <div className="bf-form-actions" style={{ marginTop: 16 }}>
          <Button icon={<ClipboardCheck />} onClick={() => setAdjusting(true)}>
            Ajustar por recuento
          </Button>
        </div>
      )}
    </Dialog>
  );
}

function AdjustDialog({ row, lots, onClose, onDone }: { row: StockRowDTO; lots: LotDTO[]; onClose: () => void; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { me, isHeadquartersUser, selectedBranchId, multiBranch } = useSession();
  const [branchId, setBranchId] = useState(
    isHeadquartersUser
      ? (selectedBranchId ?? me.branches.find((b) => b.isHeadquarters)?.id ?? me.branches[0]?.id ?? '')
      : (me.membership.branchId ?? ''),
  );
  const [counted, setCounted] = useState('');
  const [reason, setReason] = useState('Recuento físico');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Lo que el sistema tiene en esa sucursal: suma de sus lotes abiertos.
  const current = lots.filter((l) => l.branchId === branchId).reduce((a, l) => a + l.qtyOnHand, 0);
  const countedQty = counted.trim() ? toInternalQty(row.unitType, counted) : null;
  const diff = countedQty !== null ? countedQty - current : null;
  const unit = row.unitType === 'WEIGHT' ? 'kg' : 'unidades';

  async function submit() {
    const parsed = stockAdjustmentSchema.safeParse({
      productId: row.productId,
      branchId,
      countedQty: countedQty ?? -1,
      reason,
    });
    if (!parsed.success || countedQty === null) {
      const e: Record<string, string> = {};
      if (countedQty === null) e.countedQty = 'Ingresá cuánto contaste';
      if (!parsed.success) for (const i of parsed.error.issues) e[String(i.path[0])] ??= i.message;
      return setErrors(e);
    }
    setErrors({});
    setBusy(true);
    try {
      // Queda como un recuento de un solo producto: entra en el historial y avisa si el faltante es grande.
      await api.post('/control/counts', {
        branchId: parsed.data.branchId,
        notes: parsed.data.reason,
        lines: [{ productId: parsed.data.productId, countedQty: parsed.data.countedQty }],
      });
      void qc.invalidateQueries({ queryKey: ['control'] });
      toast.success('Stock ajustado', `${row.name}: ${formatQty(row.unitType, countedQty)}`);
      await invalidateInventory(qc);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title="Ajustar por recuento"
      description={`${row.name}. Ingresá lo que contaste y registramos la diferencia. Queda en la auditoría.`}
    >
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        {isHeadquartersUser && multiBranch && (
          <Field label="Sucursal" error={errors.branchId}>
            <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              {me.branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label={`Cantidad contada (${unit})`} error={errors.countedQty} hint={`El sistema tiene ${formatQty(row.unitType, current)}.`}>
          <Input
            inputMode="decimal"
            value={counted}
            onChange={(e) => setCounted(e.target.value)}
            placeholder={fromInternalQty(row.unitType, Math.max(current, 0))}
            autoFocus
          />
        </Field>
        {diff !== null && diff !== 0 && (
          <Callout tone={diff < 0 ? 'warning' : 'info'}>
            {diff < 0 ? 'Faltan' : 'Sobran'} <strong>{formatQty(row.unitType, Math.abs(diff))}</strong>.{' '}
            {diff < 0 ? 'Se descuenta primero de los lotes que vencen antes.' : 'Se suma como un lote nuevo al último costo.'}
          </Callout>
        )}
        <Field label="Motivo" error={errors.reason}>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Volver
          </Button>
          <Button variant="primary" loading={busy} disabled={diff === 0} onClick={() => void submit()}>
            Guardar ajuste
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
